import * as eventosApi from "../api/eventos";

// Backend serializa enums como string (camelCase): "calendario" | "destaque" | "ambos".
// Suporta também número (compat).
function tipoToFlags(tipo) {
  if (typeof tipo === "string") {
    const t = tipo.toLowerCase();
    return {
      calendario: t === "calendario" || t === "ambos",
      destaque: t === "destaque" || t === "ambos",
    };
  }
  const n = Number(tipo || 0);
  return { calendario: (n & 1) === 1, destaque: (n & 2) === 2 };
}

function paraLegacy(ev) {
  const { calendario, destaque } = tipoToFlags(ev?.tipoNotificacao);
  return {
    id: ev?.id,
    titulo: ev?.titulo || "",
    descricao: ev?.descricao || "",
    dataEvento: ev?.data || null,
    data: ev?.data || null,
    calendario,
    destaque,
    disciplinaIds: ev?.turmaIds || [],
    turmasIds: ev?.turmaIds || [],
    // Turmas visíveis com nome/complemento e estado individual (visto/lembrar
    // por turma) — usadas no card de destaque, um por turma.
    turmas: Array.isArray(ev?.turmas)
      ? ev.turmas.map((t) => ({
          id: t?.id,
          nome: t?.nome || "",
          complemento: t?.complemento || null,
          vistoCalendario: !!t?.vistoCalendario,
          destaqueVisto: !!t?.destaqueVisto,
          lembrarDepois: !!t?.lembrarDepois,
          // Destaque não é retroativo: false quando o aluno entrou na turma
          // depois da criação do evento (calendário ignora isso).
          destaqueElegivel: t?.destaqueElegivel !== false,
          // null = aluno não confirmou presença nesta turma.
          presencaConfirmadaEm: t?.presencaConfirmadaEm || null,
        }))
      : [],
    destino: (ev?.turmaIds || []).length > 1 ? "varias" : "uma",
    criadoPorId: ev?.criadoPorUserId || ev?.criadoPorId || null,
    // Nome do criador vem do backend (dinâmico, puxado do banco).
    criadoPorNome: ev?.criadoPorNome || "",
    instituicaoId: ev?.instituicaoId || null,
    criadoEm: ev?.criadoEm || null,
    ultimaAtualizacao: ev?.editadoEm || ev?.criadoEm || null,
    vistoCalendario: !!ev?.vistoCalendario,
    destaqueVisto: !!ev?.destaqueVisto,
    lembrarDepois: !!ev?.lembrarDepois,
    confirmacaoPresenca: !!ev?.confirmacaoPresencaHabilitada,
  };
}

function paraBackend(ev) {
  // Envia enum como string camelCase pra alinhar com o serializer do backend
  let tipo = "calendario";
  if (ev?.calendario && ev?.destaque) tipo = "ambos";
  else if (ev?.destaque) tipo = "destaque";
  else if (ev?.calendario) tipo = "calendario";

  return {
    titulo: ev?.titulo || "",
    descricao: ev?.descricao || "",
    tipoNotificacao: tipo,
    data: ev?.dataEvento || ev?.data || null,
    turmaIds: ev?.turmasIds || ev?.disciplinaIds || [],
    confirmacaoPresencaHabilitada: !!ev?.confirmacaoPresenca,
  };
}

let _cache = [];
let _loadPromise = null;

function notificar() {
  window.dispatchEvent(new Event("eventos:changed"));
  window.dispatchEvent(new Event("app:data-changed"));
}

// API nova (assíncrona)
export async function listarEventos() {
  const lista = await eventosApi.listarEventos();
  _cache = (Array.isArray(lista) ? lista : []).map(paraLegacy);
  notificar();
  return _cache;
}

export async function recarregarSeNecessario() {
  if (_cache.length > 0) return _cache;
  if (!_loadPromise) {
    _loadPromise = listarEventos().finally(() => {
      _loadPromise = null;
    });
  }
  return _loadPromise;
}

export async function criarEvento(payload) {
  const data = await eventosApi.criarEvento(paraBackend(payload));
  await listarEventos();
  return data;
}

export async function atualizarEvento(id, payload) {
  await eventosApi.editarEvento(id, { id, ...paraBackend(payload) });
  await listarEventos();
}

export async function removerEvento(id) {
  await eventosApi.excluirEvento(id);
  await listarEventos();
}

export async function marcarCalendarioVisto(id, turmaId) {
  await eventosApi.marcarCalendarioVisto(id, turmaId);
  await listarEventos();
}

export async function desmarcarCalendarioVisto(id, turmaId) {
  await eventosApi.desmarcarCalendarioVisto(id, turmaId);
  await listarEventos();
}

export async function marcarDestaqueVisto(id, turmaId) {
  await eventosApi.marcarDestaqueVisto(id, turmaId);
  await listarEventos();
}

export async function lembrarDepois(id, turmaId) {
  await eventosApi.lembrarDepois(id, turmaId);
  await listarEventos();
}

export async function confirmarPresenca(id, turmaId) {
  await eventosApi.confirmarPresenca(id, turmaId);
  await listarEventos();
}

export async function desconfirmarPresenca(id, turmaId) {
  await eventosApi.desconfirmarPresenca(id, turmaId);
  await listarEventos();
}

// Não entra no cache de eventos: é consulta pontual da tela de gestão.
export async function listarConfirmacoesPresenca(id) {
  const lista = await eventosApi.listarConfirmacoesPresenca(id);
  return Array.isArray(lista) ? lista : [];
}

// Shims sync (compat com páginas legadas pré-Bloco 5).
// Retornam direto do cache; páginas já reagem a "eventos:changed".
export function getEventos(_baseEventos = []) {
  // Dispara hidratação assíncrona no primeiro acesso, sem bloquear o render.
  if (_cache.length === 0 && !_loadPromise) {
    recarregarSeNecessario().catch(() => {});
  }
  return [..._cache];
}

export function nextEventoId() {
  return null;
}

export async function upsertEvento(evento) {
  if (!evento) return null;
  if (evento.id) {
    return atualizarEvento(evento.id, evento);
  }
  return criarEvento(evento);
}

export async function deleteEvento(id) {
  if (!id) return null;
  return removerEvento(id);
}
